import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  Column,
  Divider,
  FieldValue,
  InputOfficeDropdown,
  InputSelect,
  Label,
  Text,
} from '~/commonComponents';
import type { ISelectOption } from '~/commonComponents/InputSelect';
import { jobTitleMaxLength } from '~/components/PermanentJobsV2/utils';
import {
  useIsParentOfficeSelector,
  useProfessionOptions,
  useSpecialtyOptions,
  useUserSelector,
} from '~/hooks';
import { ProfessionTypeEnum } from '~/models';
import { filterObjectKeys } from '~/utils/Object';

export const BasicInfoForm = () => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  const isParentOffice = useIsParentOfficeSelector();
  const currentUser = useUserSelector();

  const watchOfficeSpecialty = useWatch({ control, name: 'officeSpecialty' });
  const watchProfessionType = useWatch({ control, name: 'professionType' });

  const optProfessionTypes = useProfessionOptions().map(
    ({ label, value }) => ({ title: label, value }) as ISelectOption,
  );
  const { isLoading: isGetUserEducationLoading, options: optSpecialty } = useSpecialtyOptions(
    watchProfessionType || ProfessionTypeEnum.RDH,
  );

  useEffect(() => {
    if (optSpecialty.length === 0) return;

    const isCurrentSpecialtyValid = optSpecialty.some(
      (option) => option.value === watchOfficeSpecialty,
    );

    if (!isCurrentSpecialtyValid) {
      const filteredSpecialties = filterObjectKeys(
        currentUser?.office_info?.education?.specialtiesAndProcedures,
        'specialty',
      );
      const userSpecialty = Object.keys(filteredSpecialties)?.[0];

      const defaultSpecialty = optSpecialty.some((option) => option.value === userSpecialty)
        ? userSpecialty
        : optSpecialty[0]?.value;

      if (defaultSpecialty) {
        setValue?.('officeSpecialty', defaultSpecialty, { shouldValidate: true });
      }
    }
  }, [
    watchOfficeSpecialty,
    setValue,
    watchProfessionType,
    optSpecialty,
    currentUser?.office_info?.education?.specialtiesAndProcedures,
  ]);

  return (
    <Column gap={24}>
      <Column>
        <Label text="Office Name" required={isParentOffice} />
        {isParentOffice ? (
          <Controller
            control={control}
            name="office"
            render={({ field }) => (
              <InputOfficeDropdown
                placeholder="Enter or Select Office Name"
                value={field.value || ''}
                onChange={(item) => field.onChange(item?.value)}
                error={!!errors?.office}
              />
            )}
          />
        ) : (
          <Text paddingLeft={17}>{currentUser?.office_name || ''}</Text>
        )}
      </Column>

      <Divider height={1} marginVertical={0} />

      <Column>
        <Controller
          control={control}
          name="jobTitle"
          render={({ field }) => (
            <FieldValue
              label="Job Title"
              placeholder="Enter Job Title"
              required
              shouldCapitalize={false}
              value={field.value}
              setValue={field.onChange}
              customInputStyle={{ width: '100%', boxSizing: 'border-box' }}
              validation={{ maxLength: jobTitleMaxLength }}
              error={!!errors?.jobTitle}
              errorStyle="border"
            />
          )}
        />
        <Text marginTop={8} color="neutral_500" italic>
          {jobTitleMaxLength} Character Max
        </Text>
      </Column>

      <Divider height={1} marginVertical={0} />

      <Column>
        <Label text="Professional Type" required />
        <Controller
          control={control}
          name="professionType"
          render={({ field }) => (
            <InputSelect
              placeholder="Select Professional Type"
              value={field.value}
              setValue={field.onChange}
              isSelectVisible={false}
              options={optProfessionTypes}
              error={!!errors?.professionType}
            />
          )}
        />
      </Column>

      <Column>
        <Label text="Specialty" required />
        <Controller
          control={control}
          name="officeSpecialty"
          render={({ field }) => (
            <InputSelect
              placeholder="Select Specialty"
              value={field.value}
              setValue={field.onChange}
              isSelectVisible={false}
              options={optSpecialty}
              customTitle={field.value ? field.value.split('_')?.[1] || 'General' : undefined}
              error={!!errors?.officeSpecialty}
              isLoading={isGetUserEducationLoading}
            />
          )}
        />
      </Column>
    </Column>
  );
};
